import { Cart, CartEvents, TypedCartEvent, type UpdateCartPayload } from '../internals/core';
import { MiniCart } from './mini-cart';
import { createLogger } from '@/lib/logger';

class MiniCartDiscountCode extends HTMLElement {
  private abortController: AbortController | null = null;
  private logger = createLogger('mini-cart-discount-code');
  private parentMiniCart: MiniCart | null = null;

  private formElem: HTMLFormElement | null = null;
  private inputElem: HTMLInputElement | null = null;
  private toggleElem: HTMLButtonElement | null = null;
  private messageElem: HTMLElement | null = null;

  constructor() {
    super();
  }

  private async applyDiscountCode(code: string) {
    const trimmed = code.trim();
    if (!trimmed) return;

    window.dispatchEvent(
      new CustomEvent('mini-cart:update', {
        detail: { discount: trimmed },
      }),
    );
  }

  private onSubmit(e: Event) {
    e.preventDefault();
    this.clearMessage();
    this.applyDiscountCode(this.inputElem?.value ?? '').catch((error) => {
      this.logger.error('Discount code apply failed', error);
    });
  }

  private onToggleClick(e: Event) {
    e.preventDefault();
    this.clearMessage();
    if (this.toggleElem && this.formElem) {
      this.toggleElem.style.display = 'none';
      this.formElem.style.display = 'flex';
    }
  }

  private showMessage(text: string) {
    if (!this.messageElem) return;
    this.messageElem.textContent = text;
    this.messageElem.removeAttribute('hidden');
  }

  private clearMessage() {
    if (!this.messageElem) return;
    this.messageElem.textContent = '';
    this.messageElem.setAttribute('hidden', '');
  }

  private onUpdateSuccess(e: TypedCartEvent<'cart:update:success'>) {
    const payload = e.detail?.payload as UpdateCartPayload | undefined;
    const cart = e.detail?.cart;
    if (!payload || !('discount' in payload) || payload.discount === undefined || !cart) {
      return;
    }
    const appliedCode = payload.discount.trim().toLowerCase();
    const discountCodes = (cart.discount_codes ?? []) as Array<{
      code?: string;
      applicable?: boolean;
    }>;
    const entry = discountCodes.find((d) => d?.code?.toLowerCase() === appliedCode);
    if (entry && entry.applicable === false) {
      this.showMessage("This discount code isn't applicable to your cart.");
    } else {
      this.clearMessage();
    }
  }

  private updateVisibility(itemCount: number) {
    this.classList.toggle('is-hidden', itemCount === 0);
  }

  private onMiniCartChange(cart: Cart) {
    this.updateVisibility(cart.item_count);
  }

  private bindEvents() {
    if (this.formElem) {
      this.formElem.addEventListener('submit', this.onSubmit.bind(this), {
        signal: this.abortController?.signal,
      });
    }

    if (this.inputElem) {
      this.inputElem.addEventListener('input', () => this.clearMessage(), {
        signal: this.abortController?.signal,
      });
    }

    this.parentMiniCart?.bus.on(
      CartEvents.DebouncedChange,
      (e: TypedCartEvent<'cart:changed:debounced'>) => {
        this.onMiniCartChange(e.detail.cart);
      },
    );

    this.parentMiniCart?.bus.on(
      CartEvents.UpdateSuccess,
      (e: TypedCartEvent<'cart:update:success'>) => this.onUpdateSuccess(e),
    );

    if (this.toggleElem) {
      this.toggleElem.addEventListener('click', this.onToggleClick.bind(this), {
        signal: this.abortController?.signal,
      });
    }
  }

  private unbindEvents() {
    // AbortController handles DOM listener cleanup.
  }

  private onMiniCartReady(parentMiniCart: MiniCart) {
    this.parentMiniCart = parentMiniCart;
    this.formElem = this.querySelector('[data-discount-form]') as HTMLFormElement | null;
    this.inputElem = this.querySelector('[data-discount-input]') as HTMLInputElement | null;
    this.toggleElem = this.querySelector('[data-discount-toggle]') as HTMLButtonElement | null;
    this.messageElem = this.querySelector('[data-discount-message]');
    this.bindEvents();
  }

  connectedCallback() {
    this.abortController = new AbortController();

    const parentMiniCart = this.closest('mini-cart')! as MiniCart;
    if (!parentMiniCart) {
      this.logger.error('MiniCartDiscountCode must be a child of a MiniCart element');
      throw new Error('MiniCartDiscountCode must be a child of a MiniCart element');
    }

    parentMiniCart.execOnReady(this.onMiniCartReady.bind(this, parentMiniCart));
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.unbindEvents();
  }
}

if (!customElements.get('mini-cart-discount-code')) {
  customElements.define('mini-cart-discount-code', MiniCartDiscountCode);
}
